import React from 'react';
import { FieldRenderProps } from '@progress/kendo-react-form';
import { FormFieldWrapper, FormFieldWrapperProps } from '../FormFieldWrapper';
import { Input, InputChangeEvent } from '@progress/kendo-react-inputs';
import { Button } from '@progress/kendo-react-buttons';
import { searchIcon } from '@progress/kendo-svg-icons';

interface FormSearchableInputProps extends FieldRenderProps {
  min?: number;
  max?: number;
  isLoading?: boolean;
  onSearch?: (value: string) => void;
  showSearchIcon?: boolean;
  renderFieldWrapper?: (props: FormFieldWrapperProps) => React.ReactNode;
}

export const FormSearchableInput: React.FC<FormSearchableInputProps> = fieldRenderProps => {
  const { min, max, isLoading, onSearch, disabled, showSearchIcon = true, renderFieldWrapper } = fieldRenderProps;
  const shouldShowSearchIcon = showSearchIcon;

  const handleChange = (event: InputChangeEvent) => {
    fieldRenderProps.onChange?.(event);
  };

  const handleSearchClick = () => {
    if (shouldShowSearchIcon && onSearch && fieldRenderProps.value) {
      onSearch(String(fieldRenderProps.value));
    }
  };

  const handleKeyDown = (event: { key: string }) => {
    if (event.key === 'Enter' && shouldShowSearchIcon && onSearch && fieldRenderProps.value) {
      onSearch(String(fieldRenderProps.value));
    }
  };

  const wrapperProps: FormFieldWrapperProps = {
    leadingActionButtons: shouldShowSearchIcon ? (
      <Button
        type="button"
        svgIcon={searchIcon}
        fillMode="flat"
        size="small"
        onClick={handleSearchClick}
        className="cpsFieldActionButtonCompact"
        title="Search"
        aria-label="Search"
      />
    ) : undefined,
    children: (
      <div className="cpsSearchableInputWrapper">
        <Input
          name={fieldRenderProps.name}
          valid={fieldRenderProps.valid}
          value={fieldRenderProps.value || ''}
          defaultValue={fieldRenderProps.defaultValue}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          validationMessage={fieldRenderProps.validationMessage || undefined}
          placeholder="Enter value and click search..."
          disabled={disabled || isLoading}
          minLength={min}
          maxLength={max}
        />
      </div>
    ),
    validationMessage: fieldRenderProps.validationMessage,
    value: fieldRenderProps.value,
    touched: fieldRenderProps.touched,
    id: fieldRenderProps.id,
    valid: fieldRenderProps.valid,
    disabled: fieldRenderProps.disabled,
    hint: fieldRenderProps.hint,
    label: fieldRenderProps.label,
    wrapperStyle: fieldRenderProps.wrapperStyle,
    optional: fieldRenderProps.optional,
    modified: fieldRenderProps.modified,
    visited: fieldRenderProps.visited,
    onChange: fieldRenderProps.onChange,
    onFocus: fieldRenderProps.onFocus,
    onBlur: fieldRenderProps.onBlur,
    name: fieldRenderProps.name,
    isLoading
  };

  if (renderFieldWrapper) {
    return renderFieldWrapper(wrapperProps);
  }

  return (
    <FormFieldWrapper
      validationMessage={wrapperProps.validationMessage}
      value={wrapperProps.value}
      touched={wrapperProps.touched}
      id={wrapperProps.id}
      valid={wrapperProps.valid}
      disabled={wrapperProps.disabled}
      hint={wrapperProps.hint}
      label={wrapperProps.label}
      wrapperStyle={wrapperProps.wrapperStyle}
      optional={wrapperProps.optional}
      modified={wrapperProps.modified}
      visited={wrapperProps.visited}
      onChange={wrapperProps.onChange}
      onFocus={wrapperProps.onFocus}
      onBlur={wrapperProps.onBlur}
      name={wrapperProps.name}
      isLoading={wrapperProps.isLoading}
      leadingActionButtons={wrapperProps.leadingActionButtons}
    >
      {wrapperProps.children}
    </FormFieldWrapper>
  );
};
